
import { FORMATTING_INSTRUCTIONS } from './formattingInstructions';

// Messaging Guide stage templates

export const messagingTemplates = {
  messageDevelopment: `${FORMATTING_INSTRUCTIONS}

Objective: Create comprehensive messaging guide for {campaign_name} using frame analysis and Schwartz values mapping to develop targeted campaign messages.

You are a cognitive linguist and progressive communications strategist with expertise in values-based messaging and behavioral psychology. Synthesize previous analysis into concrete, tested messaging recommendations.

Campaign Context:
- Issue: {political_issue}
- Target Entity: {target_entity}
- Campaign Type: {campaign_type}
- Desired Outcome: {winning_aspiration}
- Primary Audience: {target_audience}
- Community Context: {community_context}

Frame Analysis and Strategy Context:
{previous_analysis}

Comprehensive Message Development Tasks:

1. STRATEGIC MESSAGE ARCHITECTURE:
Develop five core 150-word messages with sophisticated targeting:

A. VALUES-BASED MESSAGE:
- Primary Schwartz Values: Focus on universalism, benevolence, and self-direction
- Moral Framework: Justice, fairness, and democratic participation
- Target Audience: {target_audience} and aligned community members
- Emotional Tone: Inspirational, righteous, empowering
- Call to Action: Clear participation pathway

B. IDENTITY-BASED MESSAGE:
- Group Identity: Union solidarity, community pride, worker dignity
- Historical Connection: Link to labor movement history and victories
- Geographic Pride: Local/regional identity and leadership potential
- Cultural Values: Tradition, loyalty, collective strength
- Future Vision: What success means for community identity

C. URGENCY-SOLUTION MESSAGE:
- Crisis Framing: Clear stakes and consequences of inaction
- Temporal Pressure: Timeline urgency and opportunity windows
- Solution Clarity: Specific pathway to resolving the issue
- Efficacy Messaging: Confidence that action will produce results
- Agency Activation: Empowering individual and collective action

D. ACCOUNTABILITY-DRIVEN MESSAGE:
- Power Analysis: Expose unfair influence and corruption
- Democratic Deficit: Highlight excluded voices and interests
- Elite Accountability: Demand transparency and responsiveness
- System Change: Challenge existing power arrangements
- Popular Sovereignty: Assert democratic control over decisions

E. ECONOMIC JUSTICE MESSAGE:
- Economic Impact: Clear material benefits of campaign success
- Fairness Frame: Economic inequality and redistribution
- Worker Power: Connection to broader labor rights agenda
- Community Investment: Local economic development and opportunity
- Future Security: Long-term economic stability and growth

2. OPPOSITION MESSAGE SIMULATION:
Create persuasive opposition message that:
- Reflects genuine concerns and reasonable objections
- Uses strongest opposition frames and emotional appeals
- Incorporates opposition research findings
- Tests campaign message vulnerabilities
- Provides inoculation preparation for campaign messages

3. COMPREHENSIVE MESSAGE MATRIX:
For each of the five core messages, provide:

Message Title and Core Narrative
Primary Frame Utilized (from frame analysis)
Schwartz Values Profile:
- Primary values emphasized (2-3)
- Secondary values supported (1-2)
- Values avoided or de-emphasized

Psychological Appeals:
- Emotional triggers and motivational drivers
- Cognitive biases leveraged
- Social proof and authority elements

Implementation Specifications:
- Optimal format (video, audio, text, visual)
- Preferred messenger characteristics
- Channel recommendations
- Timing considerations

Efficacy Assessment (scored 0-100):
- Value Alignment Score: Fit with target audience values
- Emotional Resonance Score: Likelihood of emotional engagement
- Clarity Score: Message comprehension and memorability
- Action Motivation Score: Effectiveness at driving behavior
- Risk Assessment Score: Vulnerability to opposition attack

Detailed Scoring Rationale:
- Empirical basis for effectiveness predictions
- Comparison to similar successful messages
- Identification of potential weaknesses
- Suggested testing and refinement approaches

4. MESSAGE TESTING AND REFINEMENT FRAMEWORK:
Design systematic approach to message optimization:
- Focus Group Design: Representative audience selection and protocols
- A/B Testing Framework: Digital testing methodologies
- Performance Metrics: Quantitative and qualitative assessment criteria
- Iteration Cycles: Systematic improvement processes
- Opposition Testing: Anticipating and preparing for counter-messaging

5. MESSENGER STRATEGY AND CREDIBILITY:
Identify optimal messengers for each message:
- Spokesperson Matching: Aligning messenger credibility with message content
- Community Validators: Local voices with authentic authority
- Expert Endorsers: Technical or moral authority figures
- Peer Messengers: Representative audience members
- Coalition Messengers: Allied organization representatives

6. IMPLEMENTATION AND DEPLOYMENT GUIDANCE:
Provide detailed rollout strategy:
- Message Sequencing: Order and timing of message deployment
- Channel Integration: Coordinated multi-platform messaging
- Audience Targeting: Matching specific messages to audience segments
- Intensity Modulation: Adjusting message frequency and prominence
- Crisis Adaptation: Modifying messaging during challenging periods

7. COUNTER-MESSAGING AND INOCULATION:
Prepare defenses against opposition messaging:
- Anticipated Attacks: Likely opposition responses to each message
- Preemptive Responses: Prepared counter-arguments and evidence
- Inoculation Techniques: Building resistance to opposition messaging
- Rapid Response: Quick reaction protocols for message attacks
- Narrative Recovery: Returning to positive messaging after attacks

8. CULTURAL AND CONTEXTUAL ADAPTATION:
Ensure messages work across diverse contexts:
- Cultural Sensitivity: Adapting to different community cultures
- Generational Targeting: Age-specific message variations
- Economic Context: Class-conscious messaging approaches
- Geographic Variation: Urban/rural and regional adaptations
- Coalition Coordination: Messages that work across alliance groups

Provide comprehensive messaging guide with specific implementation recommendations, testing protocols, and adaptation strategies.
`
};
